<app-modal title="Subscriptions" [dismissable]="!forcedMode">
  <div class="modal-content">
    <div *ngIf="forcedMode" class="alert alert-info d-flex align-items-center mb-3">
      <i class="ti ti-info-circle me-2 fs-5"></i>
      <span>To access the platform, please subscribe to a plan.</span>
    </div>
    <div *ngIf="viewStatus === ViewStatus.LOADING" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
    </div>
    <div *ngIf="viewStatus === ViewStatus.ERROR" class="alert alert-danger">
      Failed to load data. Please try again.
    </div>
    <div *ngIf="viewStatus === ViewStatus.READY" class="row g-3">
      <div class="col-md-4" *ngFor="let plan of plans">
        <ng-container *ngIf="getSubscription(plan) as sub; else notSubscribed">
          <!-- Active plan card -->
          <div class="card h-100 border-primary">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h6 class="mb-0">{{ plan.name }}</h6>
              <span class="badge" [ngClass]="badgeClass(sub)">{{ badgeLabel(sub) }}</span>
            </div>
            <div class="card-body d-flex flex-column">
              <p class="text-muted small">{{ plan.description }}</p>
              <div class="mb-2">
                <span class="fs-4 fw-bold">{{ formatPrice(plan.price) }}</span>
                <span class="text-muted small ms-1">/ {{ plan.billingInterval === 'MONTHLY' ? 'month' : 'year' }}</span>
              </div>
              <!-- Subscription dates -->
              <ul class="list-unstyled mb-2 small">
                <li *ngIf="isTrialPeriodActive(sub)" class="mb-1">
                  <i class="ti ti-clock text-info me-1" style="font-size:16px"></i>
                  Trial ends: <strong>{{ sub.trialEndsAt | date:'dd/MM/yyyy':'':'en-US' }}</strong>
                </li>
                <li *ngIf="sub.expiresAt" class="mb-1">
                  <i class="ti ti-calendar text-muted me-1" style="font-size:16px"></i>
                  {{ isRenewalOn(sub) ? 'Renews:' : 'Ends:' }} <strong>{{ sub.expiresAt | date:'dd/MM/yyyy':'':'en-US' }}</strong>
                </li>
                <li *ngIf="sub.cancelledAt" class="mb-1">
                  <i class="ti ti-calendar-off text-warning " style="font-size:16px"></i>
                  Cancelled: <strong>{{ sub.cancelledAt | date:'dd/MM/yyyy':'':'en-US' }}</strong>
                </li>
              </ul>
              <!-- Product prices -->
              <div class="mb-3 flex-grow-1" *ngIf="plan.productPrices?.length">
                <div class="text-muted small fw-semibold mb-1">Per-lead costs</div>
                <div class="text-muted mb-2" style="font-size: 0.75rem;">
                  Charged only when you act on a lead, on top of your plan fee.
                </div>
                <ul class="list-unstyled small mb-0">
                  <li *ngFor="let pp of plan.productPrices" class="d-flex justify-content-between mb-1">
                    <span>{{ productLabel(pp) }}</span>
                    <span class="text-muted ms-2 text-nowrap">{{ productPriceLabel(pp) }}</span>
                  </li>
                </ul>
                <div *ngIf="hasConditionalPrice(plan)" class="text-muted mt-2" style="font-size: 0.75rem;">
                  Accepting is free when your clinic is selected for that request; otherwise the
                  listed cost applies.
                </div>
              </div>
              <!-- Pending cancellation notice -->
              <div *ngIf="sub.status === SubscriptionStatus.PENDING_CANCELLATION" class="alert alert-warning py-2 px-3 small mb-2">
                <i class="ti ti-alert-triangle me-1"></i>
                <ng-container *ngIf="isTrialPeriodActive(sub); else paidNotice">
                  Auto-renewal is off: your trial stays available until the date above and then ends, with no charge.
                </ng-container>
                <ng-template #paidNotice>
                  Auto-renewal is off: this subscription will not renew at the end of the current billing period.
                </ng-template>
              </div>
              <!-- Auto-renewal toggle: unico controllo della subscription (spegnerlo la cancella, riaccenderlo la ripristina) -->
              <div *ngIf="canManageRenewal(plan)" class="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-2 bg-light">
                <div>
                  <div class="small fw-semibold">Auto-renewal</div>
                  <div class="text-muted" style="font-size: 0.75rem;">
                    {{ renewalHint(sub) }}
                  </div>
                </div>
                <div class="form-check form-switch mb-0 ms-3">
                  <input class="form-check-input" type="checkbox" role="switch"
                    [id]="'autoRenewal_' + plan.id"
                    [checked]="isRenewalOn(sub)"
                    [disabled]="togglingRenewalPlanId === plan.id"
                    (change)="onRenewalToggle(plan, $event, disableRenewalModal)">
                </div>
              </div>
            </div>
          </div>
        </ng-container>
        <!-- Not subscribed card -->
        <ng-template #notSubscribed>
          <div class="card h-100 shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">{{ plan.name }}</h6>
            </div>
            <div class="card-body d-flex flex-column">
              <p class="text-muted small">{{ plan.description }}</p>
              <div class="mb-2">
                <span class="fs-4 fw-bold">{{ formatPrice(plan.price) }}</span>
                <span class="text-muted small ms-1">/ {{ plan.billingInterval === 'MONTHLY' ? 'month' : 'year' }}</span>
              </div>
              <div *ngIf="plan.trialDays > 0 && plan.trialEligible" class="mb-2">
                <span class="badge bg-info">{{ plan.trialDays }} days free trial</span>
              </div>
              <!-- Product prices -->
              <div class=" flex-grow-1" *ngIf="plan.productPrices?.length">
                <div class="text-muted small fw-semibold mb-1">Per-lead costs</div>
                <div class="text-muted mb-2" style="font-size: 0.75rem;">
                  Charged only when you act on a lead, on top of your plan fee.
                </div>
                <ul class="list-unstyled small mb-0">
                  <li *ngFor="let pp of plan.productPrices" class="d-flex justify-content-between mb-1">
                    <span>{{ productLabel(pp) }}</span>
                    <span class="text-muted ms-2 text-nowrap">{{ productPriceLabel(pp) }}</span>
                  </li>
                </ul>
                <div *ngIf="hasConditionalPrice(plan)" class="text-muted mt-2" style="font-size: 0.75rem;">
                  Accepting is free when your clinic is selected for that request; otherwise the
                  listed cost applies.
                </div>
              </div>
              <button class="btn btn-primary btn-sm w-100 mt-auto" [disabled]="processingPlanId === plan.id" (click)="subscribe(plan)">
                <span *ngIf="processingPlanId === plan.id" class="spinner-border spinner-border-sm me-1"></span>
                {{ plan.trialDays > 0 && plan.trialEligible ? 'Start free trial' : 'Subscribe' }}
              </button>
            </div>
          </div>
        </ng-template>
      </div>
      <div *ngIf="plans.length === 0" class="col-12">
        <app-empty-state title="No plans available" message="There are no subscription plans available at the moment." iconPath="/assets/emptyIcon.png"></app-empty-state>
      </div>
    </div>
  </div>
</app-modal>
<!-- Disable auto-renewal confirmation modal -->
<ng-template #disableRenewalModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">Turn off auto-renewal</h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <ng-container *ngIf="planToDisableRenewal && getSubscription(planToDisableRenewal) as sub">
      <ng-container *ngIf="isTrialPeriodActive(sub); else paidBody">
        <p>Turn off auto-renewal for this plan? Your free trial stays available until
          <strong>{{ sub.trialEndsAt | date:'dd/MM/yyyy':'':'en-US' }}</strong> and then ends automatically,
          with no charge.</p>
      </ng-container>
      <ng-template #paidBody>
        <p>Turn off auto-renewal for this plan? It will not renew at the end of the current billing period.</p>
        <p *ngIf="sub.expiresAt" class="text-muted small mb-0">
          Your subscription will remain active until {{ sub.expiresAt | date:'dd/MM/yyyy':'':'en-US' }}.
        </p>
      </ng-template>
    </ng-container>
    <p class="text-muted small mb-0 mt-2">You can turn it back on at any time before that date.</p>
  </div>
  <div class="modal-footer">
    <button class="btn btn-secondary" (click)="modal.dismiss()">Keep auto-renewal</button>
    <button class="btn btn-danger" (click)="modal.close()">Turn off</button>
  </div>
</ng-template>
